import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import { auth } from '@/shared/api/firebase_client';
import { get } from '@/shared/api/api_client';
import type { User } from '@/entities/user';

interface AuthState {
  firebaseUser: FirebaseUser | null;
  appUser: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  setAppUser: (user: User | null) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): React.ReactElement {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [appUser, setAppUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);

      if (!fbUser) {
        setAppUser(null);
        setIsLoading(false);
        return;
      }

      // Keep the loading screen up until we have the full app user profile
      // (role, familyId, memberId, etc.) so the navbar and router render correctly.
      setIsLoading(true);
      try {
        const data = await get<{ user: User }>('/auth/me');
        setAppUser(data.user ?? null);
      } catch {
        // 404 = new user not yet registered; keep appUser null so the router
        // sends them to onboarding. Other errors are silent — pages will show
        // unauthenticated state and the user can retry.
        setAppUser(null);
      } finally {
        setIsLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    await firebaseSignOut(auth);
    setAppUser(null);
  }, []);

  // Re-fetch the user profile from the backend — useful when the server-side
  // state changes (e.g. family created) without a full Firebase auth cycle.
  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      const data = await get<{ user: User }>('/auth/me');
      setAppUser(data.user);
    } catch {
      // Leave appUser as-is on error; caller can handle if needed.
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      firebaseUser,
      appUser,
      isLoading,
      isAuthenticated: firebaseUser !== null,
      signIn,
      signOut,
      setAppUser,
      refreshUser,
    }),
    [firebaseUser, appUser, isLoading, signIn, signOut, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
